import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService } from '@taiga-ui/core';
import { TuiLanguageSwitcherService } from '@taiga-ui/i18n';
import { TuiPushService } from '@taiga-ui/kit';
import { take } from 'rxjs';

import { AccessService, getKeysFromBinary } from '@common/services/access.service';
import {
  AppLanguage,
  mapLanguageToTui,
  saveLanguageToStorage,
  SUPPORTED_LANGUAGES,
} from '@common/utils/lang';
import { GeozonesService } from '@services/geozones.service';
import { NotificationsHelperService } from '@services/notifications-helper.service';
import { ObjectsService } from '@services/objects.service';
import { UsersService } from '@services/users.service';
import { storeCurrentUser } from '@store/actions/current_user.actions';
import { changeListImportObjects } from '@store/actions/import_objects.actions';
import { addObject, deleteObjectById, updateObjectById } from '@store/actions/objects.actions';
import { updateMultipleDrivers } from '@store/drivers/drivers.actions';
import { selectAllDrivers } from '@store/drivers/drivers.selectors';
import {
  addGeozone,
  deleteGeozoneById,
  hardChangeGeozoneToVisibleOnMap,
  initGeozonesVisibleOnMap,
  updateGeozoneById,
} from '@store/geozones/geozones.actions';
import {
  createMonitoringItemFromRawObject,
  deleteMonitoringItem,
  patchShowOnMap,
  syncMonitoringFromSocket,
  updateMonitoringFromRawObject,
  updateOneMonitoring,
} from '@store/monitoring/monitoring.actions';
import { updateIsActiveNotifications } from '@store/notifications/notifications.actions';
import { updateMultipleTrailers } from '@store/trailers/trailers.actions';
import { selectAllTrailers } from '@store/trailers/trailers.selectors';

import { MaintenanceSidebarMarkerService } from '../app/portal/pages/maintenance-page/services/maintenance-sidebar-marker.service';
import { MonitoringService } from '../app/portal/pages/monitoring-page/monitoring.service';
import { environment } from '../environments/environment';

export interface SocketDataType {
  userId: number;
  type: SocketDataTypeE;
  data: any;
}

export enum SocketDataTypeE {
  /** Бэкенд ещё шлёт этот тип, хотя фронт больше не обновляет мониторинг по сокету. */
  LIVE_NEW_DATA = 'live_new_data',
  LIVE_IMPORT_DATA = 'live_import_data',
  NOTIFICATION_COUNTER = 'notification_counter',
  NEW_NOTIFICATION_PUSH = 'new_notification_push',
  COMMAND_RESULT = 'command_result',
  COMMAND_RESPONSE = 'command_response',
  OBJECTS_REFRESH = 'objects__refresh',
  OBJECT_CARD_UPDATE = 'object_card_update',
  OBJECT_MAP_VISIBILITY = 'objects__map_visibility',
  MONITORING_LIST_UPD = 'monitoring_list_upd',
  DRIVERS_ASSIGNMENT_UPD = 'drivers_assignment_upd',
  TRAILERS_ASSIGNMENT_UPD = 'trailers_assignment_upd',
  CURRENT_USER_REFRESH = 'current_user__refresh',
  NOTIFICATION_REFRESH = 'notifications__refresh',
  GEOZONES_REFRESH = 'geozones__refresh',
  MAINTENANCE_SIDEBAR_MARKER = 'maintenance_sidebar_marker',
}

export enum SocketDataEventsE {
  Deactivate = 'deactivate',
  Delete = 'delete',
  Update = 'update',
  SelfUpdate = 'self_update',
  Activate = 'activate',
  Create = 'create',
  Export = 'export',
  Import = 'import',
  ChangePassword = 'change_password',
}

export interface ChangeObjectFromSocket {
  id: number;
  action: SocketDataEventsE;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService {
  protected readonly push = inject<TuiPushService>(TuiPushService);
  private readonly alerts = inject<TuiAlertService>(TuiAlertService);
  private readonly switcher = inject<TuiLanguageSwitcherService>(TuiLanguageSwitcherService);
  private readonly store = inject(Store);
  private readonly noticeHelpS = inject(NotificationsHelperService);
  private readonly usersService = inject(UsersService);
  private readonly translate = inject(TranslateService);
  private readonly objectsService = inject(ObjectsService);
  private readonly monitoringService = inject(MonitoringService);
  private readonly geozonesService = inject(GeozonesService);
  private readonly accessService = inject(AccessService);
  private readonly maintenanceSidebarMarker = inject(MaintenanceSidebarMarkerService);

  private userSocket: null | WebSocket = null;

  public isConnected = false;

  disconnectUserSocket() {
    this.userSocket?.close();
    this.userSocket = null;
    this.isConnected = false;
  }

  connectUserSocket(currentUserInfo: any) {
    if (!this.isConnected) {
      const userId = currentUserInfo.id;

      const token = sessionStorage.getItem(environment.tokenKey);
      this.userSocket = new WebSocket(
        `${environment.websocketUrl}live-data/${userId}?token=${token}`,
      );

      this.userSocket.onopen = () => {
        console.log(`connected ${userId} - ${new Date()}`);
        this.isConnected = true;
      };

      this.userSocket.onmessage = (data: any) => {
        const socketData: SocketDataType = JSON.parse(data.data);
        this.socketMessageHandler(socketData);
      };

      this.userSocket.onclose = () => {
        console.log(`disconnected ${userId} - ${new Date()}`);
        this.isConnected = false;

        const token = sessionStorage.getItem(environment.tokenKey);
        if (token) {
          this.connectUserSocket(currentUserInfo);
        }
      };
    }
  }

  private socketMessageHandler(socketData: SocketDataType) {
    switch (socketData.type) {
      // Обновление мониторинга переведено на поллинг, событие игнорируем,
      // пока его не выпилят на бэкенде.
      case SocketDataTypeE.LIVE_NEW_DATA:
        break;
      case SocketDataTypeE.LIVE_IMPORT_DATA:
        this.importHandler(socketData);
        break;
      case SocketDataTypeE.NOTIFICATION_COUNTER:
        this.notificationCounterHandler();
        break;
      case SocketDataTypeE.NEW_NOTIFICATION_PUSH:
        this.newNotification(socketData);
        break;
      case SocketDataTypeE.COMMAND_RESULT:
        this.commandResultHandler(socketData);
        break;
      case SocketDataTypeE.COMMAND_RESPONSE:
        this.commandResponseHandler(socketData.data);
        break;
      case SocketDataTypeE.OBJECT_MAP_VISIBILITY:
        this.setObjectMapVisibility(socketData.data);
        break;
      case SocketDataTypeE.MONITORING_LIST_UPD:
        this.setMonitoringListUpd(socketData.data);
        break;
      case SocketDataTypeE.DRIVERS_ASSIGNMENT_UPD:
        this.setDriversAssignmentUpd(socketData.data);
        break;
      case SocketDataTypeE.TRAILERS_ASSIGNMENT_UPD:
        this.setTrailersAssignmentUpd(socketData.data);
        break;
      case SocketDataTypeE.OBJECTS_REFRESH:
        this.objectsRefreshHandler(socketData);
        break;
      case SocketDataTypeE.CURRENT_USER_REFRESH:
        this.currentUserRefreshHandler(socketData);
        break;
      case SocketDataTypeE.NOTIFICATION_REFRESH:
        this.notificationsRefreshHandler(socketData);
        break;
      case SocketDataTypeE.GEOZONES_REFRESH:
        this.geozonesRefreshHandler(socketData);
        break;
      case SocketDataTypeE.MAINTENANCE_SIDEBAR_MARKER:
        this.maintenanceSidebarMarkerHandler(socketData.data);
        break;
      default:
        console.error(`Unknown socket data type: ${socketData.type}`);
        break;
    }
  }

  private newNotification(socketData: any) {
    if (socketData.showPush) {
      const data = socketData.data;
      const content = `${data.text}`;
      const label = `${data.notificationName}: ${data.objectName}`;

      this.alerts
        .open(content, {
          label: label,
          autoClose: 6000,
          appearance: 'info',
        })
        .subscribe();
    }
  }

  private commandResultHandler(data: any) {
    const command = data.data.command;
    const msg =
      this.translate.instant('Command') +
      ' "' +
      command.command.name +
      '" ' +
      this.translate.instant('was sent to object') +
      ' "' +
      command.objectName +
      '"';
    this.alerts.open(msg, { appearance: 'success' }).subscribe();
  }

  private commandResponseHandler(data: { objectName: string; responseText: string }) {
    const msg = this.translate.instant('commandResponseReceived', {
      objectName: data.objectName,
      responseText: data.responseText,
    });

    this.alerts.open(msg, { appearance: 'success' }).subscribe();
  }

  private notificationCounterHandler() {
    this.noticeHelpS.countNotifications.next(this.noticeHelpS.countNotifications.value + 1);
  }

  private maintenanceSidebarMarkerHandler(data: {
    hasSoon: boolean;
    hasOverdue: boolean;
    notificationId: number;
    userId: number;
  }) {
    const { hasSoon, hasOverdue } = data;
    this.maintenanceSidebarMarker.props$.next({ hasSoon, hasOverdue });
  }

  private importHandler(data: any) {
    this.store.dispatch(changeListImportObjects({ createdObject: data.data }));
    if (data.data?.status === 'DONE') {
      this.alerts
        .open(this.translate.instant('Import is finished'), { appearance: 'success' })
        .subscribe();
    }
  }

  private setObjectMapVisibility(data: { objectIds: number[]; state: boolean }) {
    this.store.dispatch(patchShowOnMap(data));
  }

  private setMonitoringListUpd(data: { newObjectsIds: number[]; removedObjectIds: number[] }) {
    this.store.dispatch(syncMonitoringFromSocket(data));
  }

  private setDriversAssignmentUpd(data: {
    oid: number;
    driversAttached: boolean;
    subjectIds: number[];
  }) {
    const { oid, driversAttached, subjectIds } = data;

    this.store
      .select(selectAllDrivers)
      .pipe(take(1))
      .subscribe((allDrivers) => {
        this.store
          .select((state: any) => state.monitoring?.entities?.[oid])
          .pipe(take(1))
          .subscribe((monitoringItem) => {
            const objectName = monitoringItem?.name || '';

            const driverUpdates = subjectIds.map((driverId) => ({
              id: driverId,
              changes: {
                object: driversAttached ? { id: oid, name: objectName } : null,
              },
            }));

            if (driverUpdates.length > 0) {
              this.store.dispatch(updateMultipleDrivers({ updates: driverUpdates }));
            }

            let shouldUpdateIcon = true;

            if (!driversAttached) {
              const remainingDrivers = allDrivers.filter(
                (driver) => driver.object?.id === oid && !subjectIds.includes(driver.id),
              );
              shouldUpdateIcon = remainingDrivers.length === 0;
            }

            if (shouldUpdateIcon) {
              this.store.dispatch(
                updateOneMonitoring({
                  id: oid,
                  changes: {
                    driversAttached:
                      this.monitoringService.getParamsForDriversAttached(driversAttached),
                  },
                }),
              );
            }
          });
      });
  }

  private setTrailersAssignmentUpd(data: {
    oid: number;
    trailersAttached: boolean;
    subjectIds: number[];
  }) {
    const { oid, trailersAttached, subjectIds } = data;

    this.store
      .select(selectAllTrailers)
      .pipe(take(1))
      .subscribe((allTrailers) => {
        this.store
          .select((state: any) => state.monitoring?.entities?.[oid])
          .pipe(take(1))
          .subscribe((monitoringItem) => {
            const objectName = monitoringItem?.name || '';

            const trailerUpdates = subjectIds.map((trailerId) => ({
              id: trailerId,
              changes: {
                object: trailersAttached ? { id: oid, name: objectName } : null,
              },
            }));

            if (trailerUpdates.length > 0) {
              this.store.dispatch(updateMultipleTrailers({ updates: trailerUpdates }));
            }

            let shouldUpdateIcon = true;

            if (!trailersAttached) {
              const remainingTrailers = allTrailers.filter(
                (trailer) => trailer.object?.id === oid && !subjectIds.includes(trailer.id),
              );
              shouldUpdateIcon = remainingTrailers.length === 0;
            }

            if (shouldUpdateIcon) {
              this.store.dispatch(
                updateOneMonitoring({
                  id: oid,
                  changes: {
                    trailersAttached:
                      this.monitoringService.getParamsForTrailersAttached(trailersAttached),
                  },
                }),
              );
            }
          });
      });
  }

  private objectsRefreshHandler(data: any) {
    const objectId = data?.data?.entityId || null;
    const rawObjectInfo = data?.data?.entityData || null;
    const eventType = data?.data?.eventType || null;

    const objectInfo = {
      ...rawObjectInfo,
      currentUserAccess: this.monitoringService.getCurrentUserAccessAsArray(
        rawObjectInfo?.currentUserAccess,
      ),
    };

    if (eventType === SocketDataEventsE.Update && objectInfo) {
      this.store.dispatch(updateObjectById({ updatedObject: objectInfo }));
      this.store.dispatch(updateMonitoringFromRawObject({ rawObject: objectInfo }));

      this.objectsService.changeObjectFromSocket$.next({
        id: objectId,
        action: eventType,
      });
    } else if (
      [SocketDataEventsE.Create, SocketDataEventsE.Activate].includes(eventType) &&
      objectInfo
    ) {
      this.store.dispatch(addObject({ createdObject: objectInfo }));
      this.store.dispatch(createMonitoringItemFromRawObject({ rawObject: objectInfo }));
      // ---------------
    } else if (
      [SocketDataEventsE.Delete, SocketDataEventsE.Deactivate].includes(eventType) &&
      objectId
    ) {
      this.objectsService.changeObjectFromSocket$.next({
        id: objectId,
        action: eventType,
      });

      this.store.dispatch(deleteObjectById({ deletedObjectId: objectId }));
      this.store.dispatch(deleteMonitoringItem({ id: objectId }));
    }
  }

  private currentUserRefreshHandler(data: any) {
    const rawCurrentUserInfo = data?.data?.entityData || null;

    const currentUserInfo = {
      ...rawCurrentUserInfo,
      language: SUPPORTED_LANGUAGES.includes(rawCurrentUserInfo.language)
        ? rawCurrentUserInfo.language
        : AppLanguage.English,
      commonAccesses:
        typeof rawCurrentUserInfo.commonAccesses === 'number'
          ? getKeysFromBinary(
              this.accessService.allAccessRights.common,
              rawCurrentUserInfo.commonAccesses,
            )
          : rawCurrentUserInfo.commonAccesses,
    };

    const eventType:
      | SocketDataEventsE.ChangePassword
      | SocketDataEventsE.Update
      | SocketDataEventsE.SelfUpdate
      | SocketDataEventsE.Deactivate
      | SocketDataEventsE.Delete = data?.data?.eventType || null;

    if (eventType === SocketDataEventsE.SelfUpdate && currentUserInfo) {
      this.store.dispatch(storeCurrentUser({ currentUser: currentUserInfo }));

      this.translate.use(currentUserInfo.language);
      this.switcher.setLanguage(mapLanguageToTui(currentUserInfo.language));
      saveLanguageToStorage(currentUserInfo.language);
      sessionStorage.setItem(
        'timezone',
        `UTC${currentUserInfo.timezone >= 0 ? '+' : ''}${currentUserInfo.timezone}`,
      );

      const geozoneIdsSet = new Set(currentUserInfo.visibleGeozoneIds);
      const obj = Object.fromEntries(Array.from(geozoneIdsSet, (id) => [id, true]));

      this.store.dispatch(initGeozonesVisibleOnMap({ visibleOnMap: obj }));
    }

    if (eventType === SocketDataEventsE.Update && currentUserInfo) {
      this.store.dispatch(storeCurrentUser({ currentUser: currentUserInfo }));

      this.alerts
        .open(this.translate.instant('notificationOnUpdateCurrentUserBySocket'), {
          appearance: 'warning',
          autoClose: 0,
        })
        .subscribe();
    }

    if (
      [
        SocketDataEventsE.Deactivate,
        SocketDataEventsE.Delete,
        SocketDataEventsE.ChangePassword,
      ].includes(eventType)
    ) {
      if (
        eventType === SocketDataEventsE.ChangePassword &&
        this.usersService.selfChangePassword$.getValue()
      ) {
        this.usersService.selfChangePassword$.next(false);
        return;
      }

      sessionStorage.clear();
      window.location.href = `/auth/login/?eventType=${eventType}`;
    }
  }

  private notificationsRefreshHandler(data: any) {
    const eventType = data?.data?.eventType;
    const entityId = data?.data?.entityId;
    if (eventType === SocketDataEventsE.Activate && entityId) {
      this.store.dispatch(updateIsActiveNotifications({ isActive: true, id: entityId }));
    } else if (eventType === SocketDataEventsE.Deactivate && entityId) {
      this.store.dispatch(updateIsActiveNotifications({ isActive: false, id: entityId }));
    }
  }

  private geozonesRefreshHandler(socketData: any) {
    const data = socketData.data;
    const eventType = data?.eventType;
    const entityId = data?.entityId;
    const entityData = data?.entityData || null;
    const taskId = data?.taskId || null;

    if (eventType === SocketDataEventsE.Update && entityData) {
      this.store.dispatch(updateGeozoneById({ updatedGeozone: entityData }));
    } else if (eventType === SocketDataEventsE.Create && entityData) {
      this.store.dispatch(addGeozone({ createdGeozone: entityData }));
      this.store.dispatch(
        hardChangeGeozoneToVisibleOnMap({ geozoneId: entityId, isVisible: true }),
      );
    } else if (eventType === SocketDataEventsE.Delete) {
      this.store.dispatch(deleteGeozoneById({ deletedGeozoneId: entityId }));
      this.store.dispatch(
        hardChangeGeozoneToVisibleOnMap({ geozoneId: entityId, isVisible: false }),
      );
    } else if (eventType === SocketDataEventsE.Export) {
      const message = Array.isArray(data.detail) ? data.detail.join('\n') : data.detail;

      if (taskId) {
        // const downloadButton = this.translate.instant('Download');
        // const skipButton = this.translate.instant('Skip');
        // this.push
        //   .open(message, {
        //     type: this.translate.instant('Notification'),
        //     icon: '@tui.circle-alert',
        //     buttons: [downloadButton, skipButton],
        //   })
        //   .pipe(
        //     take(1),
        //     switchMap((button) => {
        //       if (button === downloadButton) {
        //         return this.geozonesService.getExportedFileByTaskId(taskId);
        //       }
        //       return EMPTY;
        //     }),
        //   )
        //   .subscribe();
        if (this.geozonesService.unsavedFileNames.includes(data?.requestedFileName)) {
          this.geozonesService.getExportedFileByTaskId(taskId).subscribe();
          this.geozonesService.unsavedFileNames = this.geozonesService.unsavedFileNames.filter(
            (name) => name !== data?.requestedFileName,
          );
        }
      } else {
        this.alerts.open(message, { appearance: 'warning', autoClose: 0 }).subscribe();
      }
    } else if (eventType === SocketDataEventsE.Import) {
      this.geozonesService.handleGeozoneImportResult(data);
    }
  }
}
